/**
 * 目标日历共用的常量与计算
 *
 * 口径与 web 端 views/h5/project/kpi/CalendarView.vue 里内联的实现一致
 * （那份是页面内自定义的，暂未抽出来；改动任一处都要两边同步）。
 */

/** kpi日历数据类型，与后端 ba_kpi_calendar.calendar_type 对齐 */
export const CALENDAR_TYPES = {
    "1": "工作日志",
    "2": "KPI任务",
    "3": "开发节点",
    "4": "拜访客户",
    "5": "线上会议",
    "6": "线下会议",
    "7": "产品学习",
    "8": "商机客户",
    "99": "其它",
};
/** 日志行的类型值。日志参与「有没有写日志」的判定，但不是「目标」 */
export const TYPE_LOG = 1;
/** 日程的类型值：这几种走 saveSchedule 单独入库，不参与 KPI 任务的拆分与顺延 */
export const SCHEDULE_TYPES = [4, 5, 6, 7, 8, 99];

/** 状态：0待办 / 50未完成(已延期) / 100已完成 */
export const STATUS_OPTIONS = [
    {value: 0, label: "待办"},
    {value: 50, label: "未完成/延期"},
    {value: 100, label: "已完成"},
];

export function typeName(type) {
    return CALENDAR_TYPES[String(type)] || "其他";
}

export function statusName(status) {
    const hit = STATUS_OPTIONS.find(item => item.value === Number(status));
    return hit ? hit.label : "";
}

/** 是不是日程（拜访客户/线上会议/线下会议/产品学习/其它） */
export function isSchedule(item) {
    return SCHEDULE_TYPES.includes(Number(item && item.calendarType));
}

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
export function today() {
    const d = new Date();
    const pad = n => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export function dateOnly(value) {
    return value ? String(value).slice(0, 10) : "";
}

/** 月份标题：2026年09月（与 web 端日历标题同一口径，翻月时年份也要看得见） */
export function monthLabel(month) {
    if (!month) {
        return "";
    }
    const parts = String(month).split("-");
    return parts[0] + "年" + String(Number(parts[1])).padStart(2, "0") + "月";
}

/**
 * 是不是工作日：有假期记录时以假期表为准（dayType=2 调休上班算工作日），
 * 没有记录再按「非周末」判断 —— 调休的周六要上班、法定假期里的周三不上班，都得看假期表。
 */
export function isWorkday(date, holidayMap) {
    const holiday = (holidayMap || {})[date];
    if (holiday) {
        return Number(holiday.dayType) === 2;
    }
    const day = new Date(date + "T00:00:00").getDay();
    return day !== 0 && day !== 6;
}

/**
 * 当月日历格子。
 * 每个格子标出：今天 / 选中 / 有任务（数量，全完成变绿）/ 往日漏填日志 / 放假调休。
 */
export function buildGridCells(month, rows, selectedDate, todayStr, holidayMap) {
    const cells = [];
    if (!month) {
        return cells;
    }
    const parts = month.split("-").map(Number);
    const year = parts[0];
    const monthNum = parts[1];
    const firstDow = new Date(year, monthNum - 1, 1).getDay();
    const totalDays = new Date(year, monthNum, 0).getDate();
    for (let i = 0; i < firstDow; i++) {
        cells.push(null);
    }
    for (let day = 1; day <= totalDays; day++) {
        const date = month + "-" + String(day).padStart(2, "0");
        const dayRows = (rows || []).filter(row => dateOnly(row.goalDate) === date);
        const goals = dayRows.filter(row => Number(row.calendarType) !== TYPE_LOG);
        const hasLog = dayRows.some(row => Number(row.calendarType) === TYPE_LOG);
        const holiday = (holidayMap || {})[date];
        cells.push({
            date,
            day,
            isToday: date === todayStr,
            isSelected: date === selectedDate,
            taskCount: goals.length,
            allDone: goals.length > 0 && goals.every(goal => Number(goal.kpiStatus) === 100),
            // 往日没填日志且是工作日 → 标红（工作日按假期表判断）
            overdue: date < todayStr && !hasLog && isWorkday(date, holidayMap),
            // 格子右下角的假期标识：放假显示节日名（没有名称就显示「休」），调休显示「调休」
            holidayText: holiday ? (Number(holiday.dayType) === 1 ? (holiday.holidayName || "休") : "调休") : "",
            holidayType: holiday ? (Number(holiday.dayType) === 1 ? "off" : "work") : "",
        });
    }
    return cells;
}
